
import { useState, useEffect, useCallback } from 'react';
import { Form } from '@/components/dashboard/shops/types';
import { getForms, getFormById } from '@/lib/supabase/forms';

export const useFormData = (shopId: string, formId?: string) => {
  const [forms, setForms] = useState<Form[]>([]);
  const [currentForm, setCurrentForm] = useState<Form | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Charger tous les formulaires associés à une boutique
  const refreshForms = useCallback(async () => {
    if (!shopId) return [];
    
    try {
      setIsLoading(true);
      const loadedForms = await getForms(shopId);
      setForms(loadedForms);
      
      if (currentForm) {
        // Rafraîchir également le formulaire courant
        const updatedCurrentForm = loadedForms.find(f => f.id === currentForm.id);
        if (updatedCurrentForm) {
          setCurrentForm(updatedCurrentForm);
        }
      } else if (loadedForms.length > 0 && !currentForm) {
        // Sélectionner le premier formulaire par défaut si aucun n'est sélectionné
        setCurrentForm(loadedForms[0]);
      }
      
      return loadedForms;
    } catch (err) {
      console.error('Erreur lors du rafraîchissement des formulaires:', err);
      setError(err instanceof Error ? err : new Error('Erreur inconnue'));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [shopId, currentForm?.id]);

  useEffect(() => {
    const loadFormsInitial = async () => {
      try {
        setIsLoading(true);
        const loadedForms = await getForms(shopId);
        setForms(loadedForms);
        
        // Si un formId spécifique est fourni et qu'il n'y a pas encore de formulaire courant
        if (formId && !currentForm) {
          const form = loadedForms.find(f => f.id === formId) || 
                      await getFormById(formId);
          if (form) {
            setCurrentForm(form);
          }
        } else if (loadedForms.length > 0 && !currentForm) {
          // Sélectionner le premier formulaire par défaut
          setCurrentForm(loadedForms[0]);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des formulaires:', err);
        setError(err instanceof Error ? err : new Error('Erreur inconnue'));
      } finally {
        setIsLoading(false);
      }
    };

    if (shopId) {
      loadFormsInitial();
    }
  }, [shopId, formId]); // Retiré currentForm de la dépendance

  return {
    forms,
    currentForm,
    setCurrentForm,
    isLoading,
    error,
    refreshForms
  };
};
